import { IAuthorization } from "../dto/auth/Authorization.ts";
import ILedgerRepository, {
  LSAccount,
  LSAccountsList, LSBalance,
  LSList,
  LSTransactionDirection,
  LSTransactionShort,
  LSTransactionStatus,
  LSTransactionType
} from "../interfaces/ILedgerRepository.ts";
import moment from "moment";
import { 
  generateTransactionHash, 
  generateTransactionId,
  generatePurchaseId,
  generateExchangeId,
  generateTransferId,
  generateCardId,
  generateTopUpId,
  randomDelay
} from "../../utils/generators";

export default class LedgerRepositoryMock implements ILedgerRepository {
  static sessions: Map<string, IAuthorization> = new Map<
    string,
    IAuthorization
  >();

  getAccount(accountId: string): Promise<LSAccount> {
    return Promise.resolve({ id: accountId } as LSAccount);
  }

  getAccounts(/*params: LSGetAccountsParams*/): Promise<LSAccountsList> {
    return Promise.resolve([{}] as LSAccount[]);
  }

  async getTransactions(
    /*params: LSGetTransactionsParams,*/
  ): Promise<LSList<LSTransactionShort>> {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    return {
      list: [
        {
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        },{
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        },{
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        }
      ],
      meta: {
        records: 1, currentPage: 1, lastPage: 1,   minEntry: 0, maxEntry: 1
      }
    };
  }

  async getTotalBalance(accountGroup: string, currency: string): Promise<LSBalance> {
    await new Promise((resolve) => setTimeout(resolve, 3000));

    if (accountGroup === 'crypto') {
      return {
        totalAmount: 0,
        onholdAmount: 0,
        availableAmount: 0,
        currencyCode: currency,
      };
    }

    return {
      totalAmount: 2110099,
      onholdAmount: 0,
      availableAmount: 0,
      currencyCode: currency,
    };
  }

  async getTotalBalances(_currency: string): Promise<LSBalance[]> {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    
    return [
      {
        totalAmount: 2110099,
        onholdAmount: 0,
        availableAmount: 2110099,
        currencyCode: 'USD',
      },
      {
        totalAmount: 1855000,
        onholdAmount: 0,
        availableAmount: 1855000,
        currencyCode: 'EUR',
      },
      {
        totalAmount: 450000,
        onholdAmount: 0,
        availableAmount: 450000,
        currencyCode: 'BTC',
      },
      {
        totalAmount: 320000,
        onholdAmount: 0,
        availableAmount: 320000,
        currencyCode: 'ETH',
      }
    ];
  }

  // Enhanced mock methods for transaction functionality
  async sendCrypto(_params: {
    fromAccount: string;
    toAddress: string;
    amount: number;
    currency: string;
    networkFee: number;
    memo?: string;
  }): Promise<{ transactionHash: string; transactionId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
    
    return {
      transactionHash: generateTransactionHash(),
      transactionId: generateTransactionId()
    };
  }

  async buyCrypto(_params: {
    cryptocurrency: string;
    amount: number;
    paymentMethod: string;
    fiatAmount: number;
  }): Promise<{ transactionId: string; purchaseId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
    
    return {
      transactionId: generateTransactionId(),
      purchaseId: generatePurchaseId()
    };
  }

  async exchangeCrypto(_params: {
    fromCurrency: string;
    toCurrency: string;
    fromAmount: number;
    toAmount: number;
    slippageTolerance: number;
  }): Promise<{ transactionHash: string; exchangeId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
    
    return {
      transactionHash: generateTransactionHash(),
      exchangeId: generateExchangeId()
    };
  }

  async sendCash(_params: {
    fromAccount: string;
    recipientName: string;
    recipientType: 'bank' | 'email' | 'phone';
    recipientDetails: any;
    amount: number;
    currency: string;
    transferMethod: string;
    memo?: string;
  }): Promise<{ transactionId: string; transferId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
    
    return {
      transactionId: generateTransactionId("TRN"),
      transferId: generateTransferId()
    };
  }

  async orderCard(_params: {
    cardType: 'virtual' | 'physical' | 'premium';
    cardholderName: string;
    linkedAccount: string;
    design?: string;
    shippingAddress?: any;
    settings: any;
  }): Promise<{ orderNumber: string; cardId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
    
    return {
      orderNumber: generateTransactionId("ORD"),
      cardId: generateCardId()
    };
  }

  async topUpAccount(_params: {
    toAccount: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    paymentDetails: any;
  }): Promise<{ transactionId: string; topUpId: string }> {
    await new Promise((resolve) => setTimeout(resolve, randomDelay(1500, 2500)));
    
    return {
      transactionId: generateTransactionId("TOP"),
      topUpId: generateTopUpId()
    };
  }

  async getTransactionHistory(params: {
    accountId?: string;
    startDate?: Date;
    endDate?: Date;
    transactionType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<LSList<LSTransactionShort>> {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    const mockTransactions: LSTransactionShort[] = [
      {
        transactionId: "trx-crypto-001",
        type: LSTransactionType.Own,
        createdAt: moment().subtract(1, 'hour'),
        signedAt: moment().subtract(1, 'hour'),
        executedAt: moment().subtract(1, 'hour'),
        fromAccountId: "acc-crypto-btc",
        toAccountId: "external-wallet",
        direction: LSTransactionDirection.Out,
        status: LSTransactionStatus.Executed,
        description: "Send Bitcoin to external wallet",
        amount: { amount: 50000, currency: "BTC" },
        feeAmount: { amount: 200, currency: "BTC" },
        documents: [],
        from: { name: "Your BTC Wallet", iban: "", swift: "", countryCode: "" },
        to: { name: "External Wallet", iban: "", swift: "", countryCode: "" }
      },
      {
        transactionId: "trx-fiat-002",
        type: LSTransactionType.Own,
        createdAt: moment().subtract(2, 'hours'),
        signedAt: moment().subtract(2, 'hours'),
        executedAt: moment().subtract(2, 'hours'),
        fromAccountId: "acc-checking-usd",
        toAccountId: "acc-external",
        direction: LSTransactionDirection.Out,
        status: LSTransactionStatus.Executed,
        description: "Wire transfer to John Smith",
        amount: { amount: 250000, currency: "USD" },
        feeAmount: { amount: 2500, currency: "USD" },
        documents: [],
        from: { name: "Your Checking Account", iban: "", swift: "", countryCode: "US" },
        to: { name: "John Smith", iban: "US123456789012345678", swift: "CHASUS33", countryCode: "US" }
      },
      {
        transactionId: "trx-crypto-003",
        type: LSTransactionType.Own,
        createdAt: moment().subtract(1, 'day'),
        signedAt: moment().subtract(1, 'day'),
        executedAt: moment().subtract(1, 'day'),
        fromAccountId: "payment-provider",
        toAccountId: "acc-crypto-eth",
        direction: LSTransactionDirection.In,
        status: LSTransactionStatus.Executed,
        description: "Purchase Ethereum with credit card",
        amount: { amount: 200000, currency: "ETH" },
        feeAmount: { amount: 2500, currency: "USD" },
        documents: [],
        from: { name: "Credit Card Payment", iban: "", swift: "", countryCode: "" },
        to: { name: "Your ETH Wallet", iban: "", swift: "", countryCode: "" }
      },
      {
        transactionId: "trx-exchange-004",
        type: LSTransactionType.Own,
        createdAt: moment().subtract(2, 'days'),
        signedAt: moment().subtract(2, 'days'),
        executedAt: moment().subtract(2, 'days'),
        fromAccountId: "acc-crypto-btc",
        toAccountId: "acc-crypto-eth",
        direction: LSTransactionDirection.Exchange,
        status: LSTransactionStatus.Executed,
        description: "Exchange BTC to ETH",
        amount: { amount: 25000, currency: "BTC" },
        feeAmount: { amount: 300, currency: "USD" },
        documents: [],
        from: { name: "Your BTC Wallet", iban: "", swift: "", countryCode: "" },
        to: { name: "Your ETH Wallet", iban: "", swift: "", countryCode: "" }
      },
      {
        transactionId: "trx-topup-005",
        type: LSTransactionType.Own,
        createdAt: moment().subtract(3, 'days'),
        signedAt: moment().subtract(3, 'days'),
        executedAt: moment().subtract(3, 'days'),
        fromAccountId: "bank-account",
        toAccountId: "acc-checking-usd",
        direction: LSTransactionDirection.In,
        status: LSTransactionStatus.Executed,
        description: "Account top-up via bank transfer",
        amount: { amount: 500000, currency: "USD" },
        feeAmount: { amount: 0, currency: "USD" },
        documents: [],
        from: { name: "External Bank Account", iban: "US987654321098765432", swift: "CHASUS33", countryCode: "US" },
        to: { name: "Your Checking Account", iban: "", swift: "", countryCode: "" }
      }
    ];

    return {
      list: mockTransactions,
      meta: {
        records: mockTransactions.length,
        currentPage: params.page || 1,
        lastPage: 1,
        minEntry: 0,
        maxEntry: mockTransactions.length
      }
    };
  }
}


